import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge, Pagination } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaStore, FaUser, FaPhone, FaGlobe, FaChartLine } from 'react-icons/fa';
import { vendorsAPI } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Vendors = () => {
    const [vendors, setVendors] = useState([]);
    const [filteredVendors, setFilteredVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        website: '',
        description: '',
        address: '',
        store_ids: []
    });

    useEffect(() => {
        fetchVendors();
    }, [pagination.page]);

    useEffect(() => {
        if (Array.isArray(vendors)) {
            const filtered = vendors.filter(vendor => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    vendor.name?.toLowerCase().includes(searchLower) ||
                    vendor.email?.toLowerCase().includes(searchLower) ||
                    vendor.phone?.toLowerCase().includes(searchLower) ||
                    vendor.website?.toLowerCase().includes(searchLower) ||
                    (vendor.description && vendor.description.toLowerCase().includes(searchLower))
                );
            });
            setFilteredVendors(filtered);
        }
    }, [searchTerm, vendors]);

    const fetchVendors = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await vendorsAPI.list({ page: pagination.page, limit: pagination.limit });

            console.log('Vendors API Response:', response.data); // Debug log

            // Handle the API response structure similar to users page
            let vendorsData = [];
            let paginationData = {};

            if (response.data && response.data.status === 'success' && response.data.data) {
                // Structure: { status: "success", data: { data: [...], meta: {...} } }
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    vendorsData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    // Structure: { status: "success", data: [...] }
                    vendorsData = response.data.data;
                }
                if (response.data.data.meta) {
                    paginationData = response.data.data.meta;
                }
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                // Alternative structure: { data: [...] }
                vendorsData = response.data.data;
            } else if (Array.isArray(response.data)) {
                // Direct array response
                vendorsData = response.data;
            }

            if (!Array.isArray(vendorsData)) {
                console.error('Invalid vendors data format:', response.data);
                vendorsData = [];
            }

            setVendors(vendorsData);
            setFilteredVendors(vendorsData);
            setPagination(prev => ({
                ...prev,
                ...paginationData,
                total: paginationData.total || vendorsData.length,
                pages: paginationData.pages || Math.ceil((paginationData.total || vendorsData.length) / prev.limit)
            }));

        } catch (err) {
            console.error('Error fetching vendors:', err);
            setError(err.response?.data?.message || 'Failed to fetch vendors');
            setVendors([]);
            setFilteredVendors([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            const payload = {
                name: formData.name,
                email: formData.email,
                password: formData.password,
                phone: formData.phone || '',
                website: formData.website || '',
                description: formData.description || '',
                address: formData.address || ''
            };

            // Only include store_ids if provided
            if (formData.store_ids.length > 0) {
                payload.store_ids = formData.store_ids.filter(id => id.trim() !== '');
            }

            await vendorsAPI.create(payload);

            setSuccessMessage('Vendor created successfully!');
            setShowCreateModal(false);
            resetForm();
            fetchVendors();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error creating vendor:', err);
            setError(err.response?.data?.message || 'Failed to create vendor');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            const payload = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone || '',
                website: formData.website || '',
                description: formData.description || '',
                address: formData.address || ''
            };

            // Only include password if provided
            if (formData.password) {
                payload.password = formData.password;
            }

            // Only include store_ids if provided
            if (formData.store_ids.length > 0) {
                payload.store_ids = formData.store_ids.filter(id => id.trim() !== '');
            }

            await vendorsAPI.update(selectedVendor.id, payload);

            setSuccessMessage('Vendor updated successfully!');
            setShowEditModal(false);
            resetForm();
            setSelectedVendor(null);
            setIsEditing(false);
            fetchVendors();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error updating vendor:', err);
            setError(err.response?.data?.message || 'Failed to update vendor');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedVendor?.id) {
            setError('Vendor ID is missing');
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            console.log('Attempting to delete vendor with ID:', selectedVendor.id);

            await vendorsAPI.delete(selectedVendor.id);

            setSuccessMessage('Vendor deleted successfully!');
            setShowDeleteModal(false);
            setSelectedVendor(null);
            fetchVendors();

            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Error deleting vendor:', err);

            let errorMsg = 'Failed to delete vendor';

            if (err.response) {
                console.error('Delete Error Response:', err.response);

                if (err.response.data) {
                    if (err.response.data.message) {
                        errorMsg = err.response.data.message;
                    } else if (err.response.data.error) {
                        errorMsg = err.response.data.error;
                    }
                }

                if (err.response.status === 403) {
                    errorMsg = 'Permission denied. You cannot delete this vendor.';
                } else if (err.response.status === 404) {
                    errorMsg = 'Vendor not found. It may have already been deleted.';
                }
            }

            setError(errorMsg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleStatusChange = async (vendorId, currentStatus) => {
        try {
            const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
            await vendorsAPI.updateStatus(vendorId, newStatus);
            fetchVendors();
        } catch (err) {
            console.error('Error changing vendor status:', err);
            setError('Failed to change vendor status');
        }
    };

    const openEditModal = (vendor) => {
        setSelectedVendor(vendor);
        setFormData({
            name: vendor.name || '',
            email: vendor.email || '',
            password: '', // Don't pre-fill password for security
            phone: vendor.phone || '',
            website: vendor.website || '',
            description: vendor.description || '',
            address: vendor.address || '',
            store_ids: getStoreIds(vendor)
        });
        setIsEditing(true);
        setShowEditModal(true);
        setError('');
    };

    const openDeleteModal = (vendor) => {
        setSelectedVendor(vendor);
        setShowDeleteModal(true);
        setError('');
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            phone: '',
            website: '',
            description: '',
            address: '',
            store_ids: []
        });
        setIsEditing(false);
        setSelectedVendor(null);
        setError('');
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return <Badge bg="success">Active</Badge>;
            case 'inactive':
                return <Badge bg="secondary">Inactive</Badge>;
            case 'pending':
                return <Badge bg="warning">Pending</Badge>;
            default:
                return <Badge bg="info">Active</Badge>;
        }
    };

    // Helper function to get store names for display
    const getStoreNames = (vendor) => {
        if (!vendor.stores || !Array.isArray(vendor.stores) || vendor.stores.length === 0) {
            return ['No stores'];
        }
        return vendor.stores.map(store => store.name);
    };

    // Helper function to get store IDs for form
    const getStoreIds = (vendor) => {
        if (!vendor.stores || !Array.isArray(vendor.stores) || vendor.stores.length === 0) {
            return [];
        }
        return vendor.stores.map(store => store.id);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const renderPagination = () => {
        if (pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Previous button
        items.push(
            <Pagination.Prev
                key="prev"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
            />
        );

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} active={1 === pagination.page} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.pages}
                    active={pagination.pages === pagination.page}
                    onClick={() => handlePageChange(pagination.pages)}
                >
                    {pagination.pages}
                </Pagination.Item>
            );
        }

        // Next button
        items.push(
            <Pagination.Next
                key="next"
                disabled={pagination.page === pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
            />
        );

        return items;
    };

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading vendors...</p>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Vendors Management</h1>
                    <p className="text-muted mb-0">Manage product vendors and their stores</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="success"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center"
                    >
                        <FaPlus className="me-2" />
                        Create Vendor
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            )}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search vendors by name, email, or phone..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                />
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end d-flex align-items-center justify-content-end">
                            <div className="me-4">
                                <small className="text-muted">
                                    Total: <strong>{pagination.total}</strong> •
                                    Showing: <strong>{searchTerm ? filteredVendors.length : vendors.length}</strong> vendors
                                </small>
                            </div>
                            {searchTerm && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setSearchTerm('')}
                                >
                                    Clear Search
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>Vendor</th>
                                <th>Contact</th>
                                <th>Details</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVendors.length > 0 ? (
                                filteredVendors.map(vendor => (
                                    <tr key={vendor.id}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                                                    style={{ width: '40px', height: '40px' }}
                                                >
                                                    <FaStore className="text-white" />
                                                </div>
                                                <div>
                                                    <div className="fw-semibold">{vendor.name || 'N/A'}</div>
                                                    <small className="text-muted">
                                                        ID: {vendor.id?.substring(0, 12)}...
                                                    </small>
                                                    {vendor.description && (
                                                        <div className="mt-1">
                                                            <small className="text-muted">{vendor.description.substring(0, 50)}...</small>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div className="fw-medium">
                                                    <FaUser className="me-1" size={12} />
                                                    {vendor.email || '-'}
                                                </div>
                                                {vendor.phone && (
                                                    <div className="text-muted">
                                                        <FaPhone className="me-1" size={10} />
                                                        {vendor.phone}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                {vendor.website && (
                                                    <div className="mb-1">
                                                        <FaGlobe className="me-1" size={12} />
                                                        <a
                                                            href={vendor.website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none"
                                                        >
                                                            Website
                                                        </a>
                                                    </div>
                                                )}
                                                <div className="mb-1">
                                                    <FaStore className="me-1" size={12} />
                                                    <span className="text-muted">
                                                        Stores: {getStoreNames(vendor).length}
                                                    </span>
                                                </div>
                                                {vendor.address && (
                                                    <div>
                                                        <small className="text-muted">{vendor.address.substring(0, 30)}...</small>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {getStatusBadge(vendor.status)}
                                                {vendor.status && (
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="ms-2 p-0"
                                                        onClick={() => handleStatusChange(vendor.id, vendor.status)}
                                                        title={`Change to ${vendor.status === 'active' ? 'inactive' : 'active'}`}
                                                    >
                                                        {vendor.status === 'active' ? (
                                                            <FaTrash className="text-danger" size={14} />
                                                        ) : (
                                                            <FaChartLine className="text-success" size={14} />
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div>{formatDate(vendor.created_at)}</div>
                                                <div className="text-muted">Updated: {formatDate(vendor.updated_at)}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openEditModal(vendor)}
                                                    title="Edit Vendor"
                                                >
                                                    <FaEdit className="me-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openDeleteModal(vendor)}
                                                    title="Delete Vendor"
                                                    disabled={actionLoading}
                                                >
                                                    <FaTrash className="me-1" />
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <div className="text-muted">
                                            {searchTerm ? (
                                                <>
                                                    <FaSearch className="h3 mb-3" />
                                                    <p>No vendors found matching "{searchTerm}"</p>
                                                </>
                                            ) : (
                                                <>
                                                    <FaStore className="h3 mb-3" />
                                                    <p>No vendors found</p>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="text-muted">
                                    Page {pagination.page} of {pagination.pages} •
                                    Showing {vendors.length} of {pagination.total} vendors
                                </small>
                            </div>
                            <Pagination className="mb-0">
                                {renderPagination()}
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Stats Summary */}
            {vendors.length > 0 && (
                <Row className="mt-4 g-3">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-primary text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">{pagination.total}</div>
                                <small>Total Vendors</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-success text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {vendors.filter(v => v.status === 'active' || !v.status).length}
                                </div>
                                <small>Active Vendors</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-info text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {vendors.filter(v => v.stores && v.stores.length > 0).length}
                                </div>
                                <small>Vendors with Stores</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-warning text-dark">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {vendors.filter(v => v.website).length}
                                </div>
                                <small>Vendors with Website</small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Create Modal */}
            <FormModal
                show={showCreateModal}
                onHide={handleModalClose}
                title="Create New Vendor"
                onSubmit={handleCreate}
                submitText="Create Vendor"
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Enter vendor name"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                placeholder="Enter email"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Password *</Form.Label>
                    <Form.Control
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        placeholder="Enter password"
                        minLength="6"
                    />
                    <Form.Text className="text-muted">
                        Password must be at least 6 characters long
                    </Form.Text>
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="Enter phone number"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Website</Form.Label>
                            <Form.Control
                                type="url"
                                value={formData.website}
                                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                placeholder="https://example.com"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter vendor description"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Enter vendor address"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Store IDs (comma separated, optional)</Form.Label>
                    <Form.Control
                        type="text"
                        value={formData.store_ids.join(', ')}
                        onChange={(e) => setFormData({
                            ...formData,
                            store_ids: e.target.value.split(',').map(s => s.trim()).filter(s => s !== '')
                        })}
                        placeholder="store_1, store_2, store_3"
                    />
                    <Form.Text className="text-muted">
                        Enter existing store IDs separated by commas. Leave empty if no stores.
                    </Form.Text>
                </Form.Group>
            </FormModal>

            {/* Edit Modal */}
            <FormModal
                show={showEditModal}
                onHide={handleModalClose}
                title={`Edit Vendor: ${selectedVendor?.name || ''}`}
                onSubmit={handleUpdate}
                submitText="Update Vendor"
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Enter vendor name"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                placeholder="Enter email"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter new password (leave blank to keep current)"
                        minLength="6"
                    />
                    <Form.Text className="text-muted">
                        Leave password blank to keep current password. Minimum 6 characters if changing.
                    </Form.Text>
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder="Enter phone number"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Website</Form.Label>
                            <Form.Control
                                type="url"
                                value={formData.website}
                                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                placeholder="https://example.com"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter vendor description"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Enter vendor address"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Store IDs (comma separated, optional)</Form.Label>
                    <Form.Control
                        type="text"
                        value={formData.store_ids.join(', ')}
                        onChange={(e) => setFormData({
                            ...formData,
                            store_ids: e.target.value.split(',').map(s => s.trim()).filter(s => s !== '')
                        })}
                        placeholder="store_1, store_2, store_3"
                    />
                    <Form.Text className="text-muted">
                        Enter existing store IDs separated by commas. Leave empty if no stores.
                    </Form.Text>
                </Form.Group>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => !actionLoading && setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Vendor"
                message={
                    <div>
                        <p>Are you sure you want to delete vendor <strong>"{selectedVendor?.name}"</strong>?</p>
                        <p className="text-danger mb-0">This action cannot be undone.</p>
                    </div>
                }
                confirmText={actionLoading ? "Deleting..." : "Delete"}
                confirmDisabled={actionLoading}
                variant="danger"
            />
        </div>
    );
};

export default Vendors;